import type { 
  ApiResponse, 
  AuthResponse, 
  LoginRequest, 
  RegisterRequest, 
  PromptsResponse,
  PromptResponse,
  CreatePromptRequest,
  UpdatePromptRequest,
  ApiError 
} from '../types/api';
import { debounce, LRUCache, RequestDeduplicator, PerformanceMonitor } from '../utils/performance';

// API配置
const API_CONFIG = {
  baseUrl: '/api',
  timeout: 30000, // 增加超时时间到30秒
  headers: {
    'Content-Type': 'application/json',
  },
};

// 性能优化实例
const apiCache = new LRUCache<string, any>(100); // 缓存100个请求结果
const requestDeduplicator = new RequestDeduplicator<any>();

// 缓存键生成函数
function generateCacheKey(endpoint: string, options: RequestInit = {}): string {
  const method = options.method || 'GET';
  const body = options.body ? JSON.stringify(options.body) : '';
  return `${method}:${endpoint}:${body}`;
}

// 优化的通用请求函数（带缓存和性能监控）
async function request<T>(
  endpoint: string, 
  options: RequestInit = {},
  cacheOptions: { 
    cacheable?: boolean; 
    cacheTTL?: number; 
    deduplicatable?: boolean 
  } = {}
): Promise<T> {
  const { cacheable = false, cacheTTL = 300000, deduplicatable = true } = cacheOptions; // 默认缓存5分钟
  const cacheKey = generateCacheKey(endpoint, options);
  const method = options.method || 'GET';
  
  // 性能监控开始
  const stopTimer = PerformanceMonitor.startTimer(`API_${method}_${endpoint}`);
  
  try {
    // GET请求尝试从缓存获取
    if (method === 'GET' && cacheable) {
      const cached = apiCache.get(cacheKey);
      if (cached) {
        console.log(`🚀 API缓存命中: ${endpoint}`);
        stopTimer();
        return cached;
      }
    }

    // 请求去重
    if (deduplicatable) {
      return await requestDeduplicator.request(cacheKey, () => executeRequest<T>(endpoint, options, cacheKey, cacheable, cacheTTL));
    } else {
      return await executeRequest<T>(endpoint, options, cacheKey, cacheable, cacheTTL);
    }
  } finally {
    stopTimer();
  }
}

// 执行实际请求的函数
async function executeRequest<T>(
  endpoint: string,
  options: RequestInit,
  cacheKey: string,
  cacheable: boolean,
  cacheTTL: number
): Promise<T> {
  const url = `${API_CONFIG.baseUrl}${endpoint}`;
  
  // 获取token
  const token = localStorage.getItem('token');
  
  // 合并headers
  const headers = {
    ...API_CONFIG.headers,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
    // 添加超时控制
    signal: AbortSignal.timeout(API_CONFIG.timeout),
  });

  const data = await response.json();

  if (!response.ok) {
    const error: ApiError = {
      message: data.message || `HTTP ${response.status}: ${response.statusText}`,
      status: response.status,
      code: data.code,
    };
    throw error;
  }

  // 缓存GET请求结果
  if ((options.method || 'GET') === 'GET' && cacheable) {
    apiCache.set(cacheKey, data);
    // 设置定时清除缓存
    setTimeout(() => {
      apiCache.delete(cacheKey);
    }, cacheTTL);
  }

  return data;
}

// 认证相关API
export const authApi = {
  login: (credentials: LoginRequest): Promise<AuthResponse> =>
    request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  register: (credentials: RegisterRequest): Promise<AuthResponse> =>
    request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  logout: (): void => {
    localStorage.removeItem('token');
  },

  // 验证token有效性
  validateToken: (): Promise<ApiResponse> =>
    request<ApiResponse>('/auth/validate'),
};

// 提示词相关API
export const promptsApi = {
  getAll: (): Promise<PromptsResponse> =>
    request<PromptsResponse>('/prompts', {}, { cacheable: true, cacheTTL: 60000 }), // 缓存1分钟

  getById: (id: number): Promise<PromptResponse> =>
    request<PromptResponse>(`/prompts/${id}`, {}, { cacheable: true, cacheTTL: 300000 }), // 缓存5分钟

  create: (prompt: CreatePromptRequest): Promise<PromptResponse> =>
    request<PromptResponse>('/prompts', {
      method: 'POST',
      body: JSON.stringify(prompt),
    }),

  update: (id: number, prompt: UpdatePromptRequest): Promise<PromptResponse> =>
    request<PromptResponse>(`/prompts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(prompt),
    }),

  delete: (id: number): Promise<ApiResponse> =>
    request<ApiResponse>(`/prompts/${id}`, {
      method: 'DELETE',
    }),

  // 搜索提示词（带防抖）
  search: debounce((query: string): Promise<PromptsResponse> =>
    request<PromptsResponse>(`/prompts/search?q=${encodeURIComponent(query)}`, {}, { cacheable: true, cacheTTL: 120000 }), // 缓存2分钟
    300), // 防抖300ms

  // 按分类获取
  getByCategory: (category: string): Promise<PromptsResponse> =>
    request<PromptsResponse>(`/prompts/category/${encodeURIComponent(category)}`, {}, { cacheable: true, cacheTTL: 300000 }), // 缓存5分钟

  // 切换提示词公开状态
  togglePublic: (id: number): Promise<ApiResponse> =>
    request<ApiResponse>(`/prompts/${id}?action=toggle-public`, {
      method: 'PATCH',
    }),

  // 获取已购买的提示词列表
  getPurchased: (): Promise<ApiResponse> =>
    request<ApiResponse>('/prompts/purchased', {}, { cacheable: false }),
};

// 提示词广场相关API
export const publicPromptsApi = {
  // 获取公共提示词（无需认证）- 带重试机制和缓存
  getPublicPrompts: async (options?: { search?: string; category?: string; limit?: number; offset?: number; sortBy?: string; _t?: number }): Promise<PromptsResponse> => {
    const params = new URLSearchParams();
    if (options?.search) params.append('search', options.search);
    if (options?.category && options.category !== '全部' && options.category !== 'all') params.append('category', options.category);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?._t) params.append('_t', options._t.toString());
    
    const queryString = params.toString();
    const endpoint = `/prompts/public${queryString ? `?${queryString}` : ''}`;
    
    // 带重试的请求
    const maxRetries = 3;
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 公共提示词请求 (尝试 ${attempt}/${maxRetries}):`, {
          endpoint,
          options,
          params: Object.fromEntries(params.entries())
        });
        
        const response = await request<PromptsResponse>(
          endpoint, 
          {}, 
          { 
            cacheable: true, 
            cacheTTL: 180000, // 缓存3分钟
            deduplicatable: true 
          }
        );
        
        console.log(`✅ 公共提示词请求成功 (尝试 ${attempt}):`, {
          dataLength: response.data?.length || 0,
          isSuccessful: response.success
        });
        
        return response;
      } catch (error) {
        lastError = error as Error;
        console.error(`❌ 公共提示词请求失败 (尝试 ${attempt}/${maxRetries}):`, {
          error: lastError.message,
          endpoint
        });
        
        // 如果不是最后一次尝试，等待后重试
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // 指数退避，最大5秒
          console.log(`⏳ ${delay}ms 后重试...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // 如果所有重试都失败了，抛出最后的错误
    throw lastError || new Error('请求失败');
  },

  // 复制公共提示词（需要认证）
  copyPrompt: (id: number): Promise<PromptResponse> =>
    request<PromptResponse>(`/prompts/public/${id}/copy`, {
      method: 'POST',
    }),
};

// 错误处理工具函数
export const handleApiError = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return (error as ApiError).message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return '发生未知错误';
};

// 检查是否是认证相关错误（用于决定是否显示toast）
export const isAuthError = (message: string): boolean => {
  const authKeywords = ['JWT', 'Token', '认证', '登录', '401', '403', '访问被拒绝'];
  return authKeywords.some(keyword => message.includes(keyword));
};

// 检查用户是否已登录
export const isUserLoggedIn = (): boolean => {
  return !!localStorage.getItem('token');
};

// API性能监控和缓存管理
export const apiPerformance = {
  // 获取性能统计
  getStats: () => PerformanceMonitor.getAllStats(),
  
  // 获取缓存统计
  getCacheStats: () => ({
    size: apiCache.size,
    maxSize: 100,
    hitRate: '需要实现命中率统计'
  }),
  
  // 清除缓存
  clearCache: () => {
    apiCache.clear();
    console.log('🧹 API缓存已清除');
    
    // 发出缓存清除事件，通知相关组件刷新
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api-cache-cleared'));
    }
  },
  
  // 预热常用接口
  warmup: async () => {
    try {
      console.log('🔥 开始API预热...');
      await Promise.all([
        publicPromptsApi.getPublicPrompts({ limit: 12, offset: 0 }),
        // 可以添加更多需要预热的接口
      ]);
      console.log('✅ API预热完成');
    } catch (error) {
      console.warn('⚠️ API预热失败:', error);
    }
  }
};

// 图片上传相关API
export const uploadApi = {
  // 上传单张图片
  uploadImage: async (file: File): Promise<ApiResponse<{ url: string; pathname: string; size: number }>> => {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`/api/upload/image?filename=${encodeURIComponent(file.name)}`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: file,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || '图片上传失败');
    }

    return data;
  },

  // 批量上传图片（最多9张）
  uploadImages: async (files: File[], onProgress?: (current: number, total: number) => void): Promise<string[]> => {
    const maxImages = 9;
    const filesToUpload = files.slice(0, maxImages);
    const uploadedUrls: string[] = [];

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      try {
        const result = await uploadApi.uploadImage(file);
        if (result.success && result.data?.url) {
          uploadedUrls.push(result.data.url);
        }
        if (onProgress) {
          onProgress(i + 1, filesToUpload.length);
        }
      } catch (error) {
        console.error(`图片 ${file.name} 上传失败:`, error);
        throw error;
      }
    }

    return uploadedUrls;
  },

  // 删除图片
  deleteImage: async (url: string): Promise<ApiResponse> => {
    const token = localStorage.getItem('token');
    
    const response = await fetch('/api/upload/image', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ url }),
    });

    return response.json();
  },
};

// 用户资料相关API
export const userApi = {
  // 获取用户资料
  getProfile: (userId?: number): Promise<ApiResponse> => {
    const endpoint = userId ? `/user/profile?user_id=${userId}` : '/user/profile';
    return request<ApiResponse>(endpoint, {}, { cacheable: true, cacheTTL: 300000 }); // 缓存5分钟
  },

  // 更新用户资料
  updateProfile: (data: { bio?: string }): Promise<ApiResponse> =>
    request<ApiResponse>('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Secret Key 相关方法
  getSecretKey: (): Promise<ApiResponse> =>
    request<ApiResponse>('/user/secret-key', {}, { cacheable: false }),

  refreshSecretKey: (): Promise<ApiResponse> =>
    request<ApiResponse>('/user/secret-key', {
      method: 'POST',
    }),

  verifySecretKey: (secretKey: string): Promise<ApiResponse> =>
    request<ApiResponse>('/user/secret-key', {
      method: 'PUT',
      body: JSON.stringify({ secret_key: secretKey }),
    }),
};

// TRAE 智能体相关API
export const agentApi = {
  // 获取智能体市场（公开列表）
  getMarket: (params: {
    search?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    industry_tag?: string;
  } = {}): Promise<ApiResponse> => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.set('search', params.search);
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.offset) queryParams.set('offset', params.offset.toString());
    if (params.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params.industry_tag) queryParams.set('industry_tag', params.industry_tag);
    
    const query = queryParams.toString();
    return request<ApiResponse>(`/agents/market${query ? `?${query}` : ''}`, {}, { cacheable: true, cacheTTL: 60000 });
  },

  // 获取智能体详情
  getAgent: (id: number): Promise<ApiResponse> =>
    request<ApiResponse>(`/agents/${id}`, {}, { cacheable: true, cacheTTL: 60000 }),

  // 获取我的智能体列表
  getMyAgents: (params: { search?: string; limit?: number; offset?: number } = {}): Promise<ApiResponse> => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.set('search', params.search);
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.offset) queryParams.set('offset', params.offset.toString());
    
    const query = queryParams.toString();
    return request<ApiResponse>(`/agents${query ? `?${query}` : ''}`, {}, { cacheable: false });
  },

  // 获取已购买的智能体列表
  getPurchasedAgents: (): Promise<ApiResponse> => 
    request<ApiResponse>('/agents/purchased', {}, { cacheable: false }),

  // 创建智能体
  createAgent: (data: {
    name: string;
    url: string;
    industryTags?: string[];
    description?: string;
    images?: string[];
    isPublic?: boolean;
    price?: number;
  }): Promise<ApiResponse> =>
    request<ApiResponse>('/agents', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 更新智能体
  updateAgent: (id: number, data: {
    name?: string;
    url?: string;
    industryTags?: string[];
    description?: string;
    images?: string[];
    isPublic?: boolean;
    price?: number;
  }): Promise<ApiResponse> =>
    request<ApiResponse>(`/agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // 删除智能体
  deleteAgent: (id: number): Promise<ApiResponse> =>
    request<ApiResponse>(`/agents/${id}`, {
      method: 'DELETE',
    }),

  // 解析TRAE分享文案
  parseShareText: (text: string): Promise<ApiResponse> =>
    request<ApiResponse>('/agents/parse', {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  // 增加使用次数
  increaseUsage: (id: number): Promise<ApiResponse<{ hotness: number }>> =>
    request<ApiResponse<{ hotness: number }>>(`/agents/${id}`, {
      method: 'PATCH',
    }),
};

// 支付相关API
export const paymentApi = {
  // 创建支付订单
  createOrder: (data: { itemType: 'prompt' | 'agent'; itemId: number; payType?: 'wxpay' | 'alipay' }): Promise<ApiResponse> =>
    request<ApiResponse>('/payment/create', {
      method: 'POST',
      body: JSON.stringify(data),
    }, { deduplicatable: false }),

  // 检查是否已购买
  checkPurchase: (itemType: 'prompt' | 'agent', itemId: number): Promise<ApiResponse> =>
    request<ApiResponse>(`/payment/check?itemType=${itemType}&itemId=${itemId}`, {}, { cacheable: true, cacheTTL: 60000 }),

  // 查询订单列表
  getOrders: (params: { limit?: number; offset?: number; status?: string } = {}): Promise<ApiResponse> => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.offset) queryParams.set('offset', params.offset.toString());
    if (params.status) queryParams.set('status', params.status);
    const query = queryParams.toString();
    return request<ApiResponse>(`/payment/orders${query ? `?${query}` : ''}`, {}, { cacheable: false });
  },

  // 查询单个订单
  getOrder: (orderNo: string, refresh = false): Promise<ApiResponse> =>
    request<ApiResponse>(`/payment/orders?orderNo=${orderNo}${refresh ? '&refresh=true' : ''}`, {}, { cacheable: false }),

  // 同步订单状态（从支付平台返回后调用）
  syncOrder: (orderNo: string, tradeNo: string): Promise<ApiResponse> =>
    request<ApiResponse>('/payment/sync', {
      method: 'POST',
      body: JSON.stringify({ orderNo, tradeNo }),
    }, { deduplicatable: false }),

  // 获取账单信息
  getBilling: (params: { type?: string; limit?: number; offset?: number } = {}): Promise<ApiResponse> => {
    const queryParams = new URLSearchParams();
    if (params.type) queryParams.set('type', params.type);
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.offset) queryParams.set('offset', params.offset.toString());
    const query = queryParams.toString();
    return request<ApiResponse>(`/payment/billing${query ? `?${query}` : ''}`, {}, { cacheable: true, cacheTTL: 60000 });
  },
};

// API实例
const apiService = {
  auth: authApi,
  prompts: promptsApi,
  publicPrompts: publicPromptsApi,
  user: userApi,
  upload: uploadApi,
  agents: agentApi,
  payment: paymentApi,
  handleError: handleApiError,
  performance: apiPerformance,
};

// 导出默认的API实例
export default apiService; 